import { Popup as AkPopup } from '@atlaskit/popup';
import ScrollableContainer from '@databases/layout/ScrollableContainer';
import Z_INDEX from '@databases/layout/z-index';
import { useResizeObserverCallback } from '@databases/react';
import React, {
  ComponentProps,
  ComponentType,
  Fragment,
  FunctionComponent,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PopperChildrenProps } from 'react-popper';
import styled from 'styled-components';

type Placement = NonNullable<ComponentProps<typeof AkPopup>['placement']>;

const opposites: Record<string, string> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
  start: 'end',
  auto: 'auto',
  end: 'start',
};

export const REDUCED_POPUP_OFFSET: [number, number] = [0, 4];

/**
 * A copy of getFallbackPlacements() from @atlaskit/dropdown-menu. Can be passed as fallbackPlacement to @atlaskit/popup.
 */
function getFallbackPlacements(placement: Placement): Placement[] | undefined {
  const p = placement.split('-');
  if (!['top', 'bottom'].includes(p[0])) {
    return undefined;
  } else if (p.length === 2 && ['start', 'end'].includes(p[1])) {
    return [
      `${p[0]}`,
      `${p[0]}-${opposites[p[1]]}`,
      `${opposites[p[0]]}-${p[1]}`,
      `${opposites[p[0]]}`,
      `${opposites[p[0]]}-${opposites[p[1]]}`,
      'auto',
    ] as Placement[];
  } else {
    return [
      `${p[0]}-start`,
      `${p[0]}-end`,
      `${opposites[p[0]]}`,
      `${opposites[p[0]]}-start`,
      `${opposites[p[0]]}-end`,
      `auto`,
    ] as Placement[];
  }
}

export const DefaultPopupContentContainer = styled(ScrollableContainer)<{
  width?: number;
}>`
  max-height: calc(100vh - 16px);
  flex-shrink: 0;

  > * {
    // ScrollableContainer InnerContainer
    padding: 16px 24px;
  }
`;

export type PopupProps = ComponentProps<typeof AkPopup> & {
  width?: number;
  isTooltip?: boolean;
  contentContainer?: ComponentType | null;
};

/**
 * Like @atlaskit/popup, with the following differences:
 * - Supports the open menu padding
 * - Uses fallback placements and a max height to become scrollable when it's higher than the viewport
 * - Shows a drop shadow when it's scrollable
 * - Detects resizes of its content and automatically repositions the popup
 */
const Popup: FunctionComponent<PopupProps> = ({
  isOpen,
  content,
  placement = 'bottom-start',
  width,
  isTooltip,
  contentContainer = DefaultPopupContentContainer,
  ...props
}) => {
  const fallbackPlacements = useMemo(() => getFallbackPlacements(placement), [placement]);

  const updateRef = useRef<PopperChildrenProps['update']>();

  const [contentRef, setContentRef] = useState<HTMLDivElement | undefined | null>(null);
  useResizeObserverCallback(contentRef, () => {
    updateRef.current?.();
  });

  return (
    <AkPopup
      isOpen={isOpen}
      placement={placement}
      zIndex={Z_INDEX.INLINE_DIALOG}
      fallbackPlacements={fallbackPlacements}
      content={(contentProps) => {
        updateRef.current = contentProps.update;

        const Wrapper =
          isTooltip || contentContainer === null ? Fragment : contentContainer;

        return (
          <Wrapper>
            <div ref={setContentRef} style={{ width }}>
              {content(contentProps)}
            </div>
          </Wrapper>
        );
      }}
      autoFocus={!isTooltip}
      {...props}
    />
  );
};

export default Popup;
